import { supabase } from '../lib/supabase';

export const packageService = {
  // Get all commercial packages with optional filters
  getAll: async (filters = {}) => {
    try {
      let query = supabase?.from('commercial_packages')?.select(`
        *,
        package_services (
          id,
          quantity,
          service:services (
            id,
            service_name,
            service_code,
            description,
            category,
            unit_price,
            is_active
          )
        )
      `);
      
      if (filters?.search) {
        query = query?.or(`package_name.ilike.%${filters?.search}%,package_code.ilike.%${filters?.search}%`);
      }
      if (filters?.tier && filters?.tier !== 'all') {
        query = query?.eq('tier', filters?.tier);
      }
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.countryId && filters?.countryId !== 'all') {
        query = query?.eq('country_id', filters?.countryId);
      }
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Fetch country data separately for packages that have country_id
      const packageIds = data?.filter(pkg => pkg?.country_id)?.map(pkg => pkg?.country_id) || [];
      let countriesMap = {};
      
      if (packageIds?.length > 0) {
        const { data: countriesData, error: countriesError } = await supabase
          ?.from('countries')
          ?.select('id, country_name, country_code, currency_code')
          ?.in('id', packageIds);
        
        if (!countriesError && countriesData) {
          countriesMap = countriesData?.reduce((acc, country) => {
            acc[country?.id] = country;
            return acc;
          }, {});
        }
      }
      
      // Convert snake_case to camelCase
      return data?.map(pkg => ({
        id: pkg?.id,
        packageName: pkg?.package_name,
        packageCode: pkg?.package_code,
        tier: pkg?.tier,
        status: pkg?.status,
        description: pkg?.description,
        basePrice: pkg?.base_price,
        discountPercentage: pkg?.discount_percentage,
        finalPrice: pkg?.final_price,
        includedUnits: pkg?.included_units,
        additionalUnitPrice: pkg?.additional_unit_price,
        features: pkg?.features,
        totalSales: pkg?.total_sales,
        designFee: pkg?.design_fee,
        revenueGenerated: pkg?.revenue_generated,
        countryId: pkg?.country_id,
        country: pkg?.country_id && countriesMap?.[pkg?.country_id] ? {
          id: countriesMap?.[pkg?.country_id]?.id,
          countryName: countriesMap?.[pkg?.country_id]?.country_name,
          countryCode: countriesMap?.[pkg?.country_id]?.country_code,
          currencyCode: countriesMap?.[pkg?.country_id]?.currency_code
        } : null,
        services: pkg?.package_services?.map(ps => ({
          id: ps?.service?.id,
          serviceName: ps?.service?.service_name,
          serviceCode: ps?.service?.service_code,
          description: ps?.service?.description,
          category: ps?.service?.category,
          unitPrice: ps?.service?.unit_price,
          isActive: ps?.service?.is_active,
          quantity: ps?.quantity
        })) || [],
        createdAt: pkg?.created_at,
        updatedAt: pkg?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching commercial packages:', error);
      throw error;
    }
  },

  // Get single package by ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase?.from('commercial_packages')?.select(`
        *,
        package_services (
          id,
          quantity,
          service:services (
            id,
            service_name,
            service_code,
            description,
            category,
            unit_price,
            is_active
          )
        )
      `)?.eq('id', id)?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        packageName: data?.package_name,
        packageCode: data?.package_code,
        tier: data?.tier,
        status: data?.status,
        description: data?.description,
        basePrice: data?.base_price,
        discountPercentage: data?.discount_percentage,
        finalPrice: data?.final_price,
        includedUnits: data?.included_units,
        additionalUnitPrice: data?.additional_unit_price,
        features: data?.features,
        totalSales: data?.total_sales,
        revenueGenerated: data?.revenue_generated,
        services: data?.package_services?.map(ps => ({
          id: ps?.service?.id,
          serviceName: ps?.service?.service_name,
          serviceCode: ps?.service?.service_code,
          description: ps?.service?.description,
          category: ps?.service?.category,
          unitPrice: ps?.service?.unit_price,
          isActive: ps?.service?.is_active,
          quantity: ps?.quantity
        })) || [],
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error fetching package ${id}:`, error);
      throw error;
    }
  },

  // Create new commercial package
  create: async (packageData) => {
    try {
      const { data, error } = await supabase?.from('commercial_packages')?.insert({
          package_name: packageData?.packageName,
          package_code: packageData?.packageCode,
          tier: packageData?.tier || 'silver',
          status: packageData?.status || 'active',
          description: packageData?.description || null,
          base_price: packageData?.basePrice,
          discount_percentage: packageData?.discountPercentage || 0,
          final_price: packageData?.finalPrice,
          included_units: packageData?.includedUnits,
          additional_unit_price: packageData?.additionalUnitPrice || null,
          features: packageData?.features || [],
          total_sales: packageData?.totalSales || 0,
          revenue_generated: packageData?.revenueGenerated || 0,
          country_id: packageData?.countryId || null
        })?.select()?.single();
      
      if (error) throw error;

      // Add services to package if provided
      if (packageData?.serviceIds && packageData?.serviceIds?.length > 0) {
        const packageServices = packageData?.serviceIds?.map(serviceId => ({
          package_id: data?.id,
          service_id: serviceId,
          quantity: packageData?.includedUnits || 1
        }));

        const { error: servicesError } = await supabase?.from('package_services')?.insert(packageServices);
        if (servicesError) throw servicesError;
      }
      
      // Fetch complete package with services
      return await packageService?.getById(data?.id);
    } catch (error) {
      console.error('Error creating commercial package:', error);
      throw error;
    }
  },

  // Update existing commercial package
  update: async (id, packageData) => {
    try {
      const updateData = {};
      if (packageData?.packageName !== undefined) updateData.package_name = packageData?.packageName;
      if (packageData?.packageCode !== undefined) updateData.package_code = packageData?.packageCode;
      if (packageData?.tier !== undefined) updateData.tier = packageData?.tier;
      if (packageData?.status !== undefined) updateData.status = packageData?.status;
      if (packageData?.description !== undefined) updateData.description = packageData?.description;
      if (packageData?.basePrice !== undefined) updateData.base_price = packageData?.basePrice;
      if (packageData?.discountPercentage !== undefined) updateData.discount_percentage = packageData?.discountPercentage;
      if (packageData?.finalPrice !== undefined) updateData.final_price = packageData?.finalPrice;
      if (packageData?.includedUnits !== undefined) updateData.included_units = packageData?.includedUnits;
      if (packageData?.additionalUnitPrice !== undefined) updateData.additional_unit_price = packageData?.additionalUnitPrice;
      if (packageData?.features !== undefined) updateData.features = packageData?.features;
      if (packageData?.totalSales !== undefined) updateData.total_sales = packageData?.totalSales;
      if (packageData?.revenueGenerated !== undefined) updateData.revenue_generated = packageData?.revenueGenerated;
      if (packageData?.countryId !== undefined) updateData.country_id = packageData?.countryId;
      if (packageData?.designFee !== undefined) updateData.design_fee = packageData?.designFee;
      
      const { error } = await supabase?.from('commercial_packages')?.update(updateData)?.eq('id', id);
      
      if (error) throw error;

      // Update services if provided
      if (packageData?.serviceIds !== undefined) {
        // Delete existing services
        await supabase?.from('package_services')?.delete()?.eq('package_id', id);

        // Add new services
        if (packageData?.serviceIds?.length > 0) {
          const packageServices = packageData?.serviceIds?.map(serviceId => ({
            package_id: id,
            service_id: serviceId,
            quantity: packageData?.includedUnits || 1
          }));

          const { error: servicesError } = await supabase?.from('package_services')?.insert(packageServices);
          if (servicesError) throw servicesError;
        }
      }
      
      // Fetch complete package with services
      return await packageService?.getById(id);
    } catch (error) {
      console.error(`Error updating commercial package ${id}:`, error);
      throw error;
    }
  },

  // Delete commercial package
  delete: async (id) => {
    try {
      const { error } = await supabase?.from('commercial_packages')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting commercial package ${id}:`, error);
      throw error;
    }
  },

  // Update package status (active/inactive)
  updateStatus: async (id, status) => {
    try {
      const { data, error } = await supabase?.from('commercial_packages')?.update({ status })?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating package status ${id}:`, error);
      throw error;
    }
  },

  // Update package pricing
  updatePricing: async (id, pricingData) => {
    try {
      const updateData = {};
      if (pricingData?.basePrice !== undefined) updateData.base_price = pricingData?.basePrice;
      if (pricingData?.discountPercentage !== undefined) updateData.discount_percentage = pricingData?.discountPercentage;
      if (pricingData?.finalPrice !== undefined) updateData.final_price = pricingData?.finalPrice;
      if (pricingData?.additionalUnitPrice !== undefined) updateData.additional_unit_price = pricingData?.additionalUnitPrice;
      
      const { data, error } = await supabase?.from('commercial_packages')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        basePrice: data?.base_price,
        discountPercentage: data?.discount_percentage,
        finalPrice: data?.final_price,
        additionalUnitPrice: data?.additional_unit_price,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating package pricing ${id}:`, error);
      throw error;
    }
  }
};

export const serviceService = {
  // Get all services
getAll: async (filters = {}) => {
  try {
    let query;
    
    // Si un pays est spécifié, utiliser inner join pour filtrer
    if (filters?.countryCode) {
      query = supabase
        ?.from('services')
        ?.select(`
          *,
          service_countries!inner (
            country_id,
            is_available,
            custom_price,
            country:countries!inner (
              id,
              country_code,
              country_name,
              currency_code
            )
          )
        `)
        ?.eq('service_countries.country.country_code', filters?.countryCode)
        ?.eq('service_countries.is_available', true);
    } else {
      // Sinon, utiliser left join pour récupérer tous les services
      query = supabase
        ?.from('services')
        ?.select(`
          *,
          service_countries (
            country_id,
            is_available,
            custom_price,
            country:countries (
              id,
              country_code,
              country_name,
              currency_code
            )
          )
        `);
    }
    
    // Filtre par catégorie
    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }
    
    // Filtre par statut actif
    if (filters?.isActive !== undefined) {
      query = query?.eq('is_active', filters?.isActive);
    }
    
    const { data, error } = await query?.order('service_name', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(service => {
      // Si countryCode est spécifié, utiliser le prix personnalisé du pays
      let finalUnitPrice = service?.unit_price;
      
      if (filters?.countryCode && service?.service_countries?.length > 0) {
        const countryService = service?.service_countries?.find(
          sc => sc?.country?.country_code === filters?.countryCode
        );
        if (countryService?.custom_price) {
          finalUnitPrice = countryService?.custom_price;
        }
      }
      
      return {
        id: service?.id,
        serviceName: service?.service_name,
        serviceCode: service?.service_code,
        description: service?.description,
        category: service?.category,
        unitPrice: finalUnitPrice, // Prix adapté au pays si applicable
        basePrice: service?.unit_price, // Prix de base original
        isActive: service?.is_active,
        createdAt: service?.created_at,
        updatedAt: service?.updated_at,
        // Informations du/des pays
        countries: service?.service_countries?.map(sc => ({
          countryId: sc?.country_id,
          countryCode: sc?.country?.country_code,
          countryName: sc?.country?.country_name,
          currencyCode: sc?.country?.currency_code,
          isAvailable: sc?.is_available,
          customPrice: sc?.custom_price || service?.unit_price
        })) || []
      };
    }) || [];
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
},

  // Get single service by ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase?.from('services')?.select('*')?.eq('id', id)?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceName: data?.service_name,
        serviceCode: data?.service_code,
        description: data?.description,
        category: data?.category,
        unitPrice: data?.unit_price,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error fetching service ${id}:`, error);
      throw error;
    }
  },

  // Create new service
  create: async (serviceData) => {
    try {
      const { data, error } = await supabase?.from('services')?.insert({
          service_name: serviceData?.serviceName,
          service_code: serviceData?.serviceCode,
          description: serviceData?.description || null,
          category: serviceData?.category,
          unit_price: serviceData?.unitPrice,
          is_active: serviceData?.isActive !== undefined ? serviceData?.isActive : true
        })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceName: data?.service_name,
        serviceCode: data?.service_code,
        description: data?.description,
        category: data?.category,
        unitPrice: data?.unit_price,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },

  // Update service
  update: async (id, serviceData) => {
    try {
      const updateData = {};
      if (serviceData?.serviceName !== undefined) updateData.service_name = serviceData?.serviceName;
      if (serviceData?.serviceCode !== undefined) updateData.service_code = serviceData?.serviceCode;
      if (serviceData?.description !== undefined) updateData.description = serviceData?.description;
      if (serviceData?.category !== undefined) updateData.category = serviceData?.category;
      if (serviceData?.unitPrice !== undefined) updateData.unit_price = serviceData?.unitPrice;
      if (serviceData?.isActive !== undefined) updateData.is_active = serviceData?.isActive;
      
      const { data, error } = await supabase?.from('services')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        serviceName: data?.service_name,
        serviceCode: data?.service_code,
        description: data?.description,
        category: data?.category,
        unitPrice: data?.unit_price,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating service ${id}:`, error);
      throw error;
    }
  },

  // Delete service
  delete: async (id) => {
    try {
      const { error } = await supabase?.from('services')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting service ${id}:`, error);
      throw error;
    }
  }
};

export default packageService;